import { spring } from 'react-motion';
import assignWith from 'lodash/assignWith';
import pickBy from 'lodash/pickBy';
import { EMPTY_OBJECT } from '../constants';

export function springify(style, options = EMPTY_OBJECT) {
  const springOptions = { precision: 0.001, ...options };
  return assignWith({}, springable(style), (_, value) => {
    return spring(value, springOptions);
    return value;
  });
}

export function springable(style) {
  return pickBy(style, Number.isFinite);
}

export function invertRange(value, start = 0, end = 1) {
  return value * -1 + start + end;
}

export function scrollToElement(
  element,
  rate = 2.5,
  minDuration = 500,
  offset = 0,
  easing = EasingFunctions.easeInOutQuint,
) {
  const bounding = element.getBoundingClientRect();
  const top = bounding.top + window.pageYOffset;
  const dest = top + offset;
  const start = window.pageYOffset;
  const duration = Math.max(
    (Math.max(start, dest) - Math.min(start, dest)) / rate,
    minDuration,
  );

  const update = function(value) {
    window.scrollTo(window.scrollX, value);
  };

  return tweenValue(start, dest, update, {
    duration: duration,
    easing: easing,
  });
}

function animateValue(duration, step, easing) {
  return new Promise(function(resolve, reject) {
    if (typeof easing != 'function') {
      if (typeof EasingFunctions[easing] !== 'function') {
        throw new Error('easing must be one of the EasingFunctions.');
      }
      easing = EasingFunctions[easing];
    }

    const start = performance.now();

    const update = function() {
      const timeFactor = Math.min((performance.now() - start) / duration, 1);
      const value = easing(timeFactor);

      step(value);

      if (timeFactor < 1) {
        window.requestAnimationFrame(update);
      } else {
        resolve();
      }
    };

    window.requestAnimationFrame(update);
  });
}

function tweenValue(initialValue, finalValue, step, options) {
  const opts = { duration: 1, easing: EasingFunctions.linear, ...options };
  const rangeOfValue = finalValue - initialValue;

  const update = function(factor) {
    const value = factor * rangeOfValue + initialValue;
    step(value);
  };

  return animateValue(opts.duration, update, opts.easing);
}

/*
 * Easing Functions - inspired from http://gizma.com/easing/
 * only considering the t value for the range [0, 1] => [0, 1]
 */
const EasingFunctions = {
  // no easing, no acceleration
  linear: function(t) {
    return t;
  },
  // accelerating from zero velocity
  easeInQuad: function(t) {
    return t * t;
  },
  // decelerating to zero velocity
  easeOutQuad: function(t) {
    return t * (2 - t);
  },
  // acceleration until halfway, then deceleration
  easeInOutQuad: function(t) {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  },
  // accelerating from zero velocity
  easeInCubic: function(t) {
    return t * t * t;
  },
  // decelerating to zero velocity
  easeOutCubic: function(t) {
    return --t * t * t + 1;
  },
  // acceleration until halfway, then deceleration
  easeInOutCubic: function(t) {
    return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
  },
  // accelerating from zero velocity
  easeInQuart: function(t) {
    return t * t * t * t;
  },
  // decelerating to zero velocity
  easeOutQuart: function(t) {
    return 1 - --t * t * t * t;
  },
  // acceleration until halfway, then deceleration
  easeInOutQuart: function(t) {
    return t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t;
  },
  // accelerating from zero velocity
  easeInQuint: function(t) {
    return t * t * t * t * t;
  },
  // decelerating to zero velocity
  easeOutQuint: function(t) {
    return 1 + --t * t * t * t * t;
  },
  // acceleration until halfway, then deceleration
  easeInOutQuint: function(t) {
    return t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * --t * t * t * t * t;
  },
};



// WEBPACK FOOTER //
// ./src/lib/Motion.js